import { useAuthStore } from '../stores/useAuthStore';

/**
 * Error thrown when authentication flow is interrupted (user dismissal, duplicate requests, etc.)
 */
export class AuthFlowError extends Error {
  constructor(message = 'Authentication flow interrupted') {
    super(message);
    this.name = 'AuthFlowError';
  }
}

/**
 * Ensures user is authenticated
 *
 * Returns a promise that resolves when complete, or rejects with AuthFlowError if interrupted.
 * See guides/AUTHENTICATION_FLOW.md for usage patterns.
 */
export const authenticateUser = (): Promise<void> => {
  return new Promise<void>((resolve, reject) => {
    const { isAuthenticated, openAuthFlowModal, modalFlow } =
      useAuthStore.getState();

    // Already authenticated - resolve immediately
    if (isAuthenticated) {
      resolve();
      return;
    }

    // Prevent duplicate modals - throw error if modal already visible
    if (modalFlow.isVisible) {
      // Modal is already showing (handles both auth and onboarding)
      // Reject to prevent duplicate actions and stop the promise chain
      console.warn('Authentication already in progress');
      reject(new AuthFlowError('Authentication already in progress'));
      return;
    }

    openAuthFlowModal({
      isDismissible: true,
      onFlowComplete: resolve,
      onFlowDismiss: () => {
        reject(new AuthFlowError('Auth flow cancelled'));
      },
    });
  });
};
